import {
  AbstractClient,
  Client,
  ClientErrorResponse,
  ClientResponse,
  ClientStatus,
  ClientUrls,
  Conversation,
  ConversationActionParams,
  ConversationChannel,
  ConversationContext,
  ConversationMessage,
  ConversationMessageStatus,
  ConversationParticipant,
  ConversationStatus,
  ConversationSyncStorageMode,
  ErrorScope,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  GetConversationParticipantsParams,
  GetConversationParticipantsResponse,
  InProgressParams,
  InProgressResponse,
  isNetworkError,
  LeaveConversationParams,
  ObservabilityConfig,
  SendConversationMessageParams,
  SendConversationTypingParams,
  StartConversationParams,
  StartConversationResponse,
} from '@stripe-internal/conversations-client';
import LRUCache from 'lru-cache';
import {sum, size, difference} from 'lodash';
import {
  Conversation as TwilioConversation,
  ConversationUpdateReason as TwilioConversationUpdateReason,
  ConnectionState as TwilioConnectionState,
  Message as TwilioMessage,
  Participant as TwilioParticipant,
} from '@stripe-internal/stripethirdparty/lib/shared/TwilioConversationsTypes';
import {STPTwilio, STPTwilioOutput} from './stp';
import {METRIC} from './metrics';

const CLIENT_TYPE = 'twilio_conversations';
const METRIC_TAGS = {client_type: CLIENT_TYPE};
const ERROR_TAGS = METRIC_TAGS;
const LOG_PREFIX = '[twilio-conversations-client]:';

const KB = 1024;

const STPUnmounted = new STPTwilio(() => {
  throw Error('STPUnmounted called');
});

export class TwilioClient extends AbstractClient implements Client {
  private service: STPTwilio = STPUnmounted;

  private messageCache: LRUCache<string, ConversationMessage> = new LRUCache({
    max: 500,
    maxSize: 512 * KB,
    sizeCalculation: (o) => sum(Object.values(o).map((m) => m?.length || 1)),
  });

  /**
   * Memorize the last fetch index for Twilio conversation messages. This should
   * be equal to the index of the newest message in a conversation because we are
   * reading from the end.
   */
  private messageAnchorMemo: Record<string, number> = {};

  constructor({
    clientFetch,
    urls,
    observability,
  }: {
    clientFetch?: typeof fetch;
    urls?: Partial<ClientUrls>;
    observability?: ObservabilityConfig;
  } = {}) {
    super({clientFetch, urls, observability});
  }

  initialize(service: STPTwilio) {
    this.service = service;
    if (this.getStatus() === ClientStatus.Uninitialized) {
      this.status$.next(ClientStatus.Initialized);
    }
  }

  get channel(): ConversationChannel {
    return 'chat';
  }

  get type() {
    return CLIENT_TYPE;
  }

  get syncStorageMode(): ConversationSyncStorageMode {
    return 'hydrate';
  }

  async disconnect(): Promise<void> {
    this.status$.next(ClientStatus.Uninitialized);
    await this.service.shutdown();
  }

  sendFeedback(): Promise<ClientResponse> {
    return this.error(400, 'sendFeedback not supported by Twilio');
  }

  async startConversation(
    params: StartConversationParams,
  ): Promise<StartConversationResponse> {
    let {conversationId: id, participant: user, token} = params;

    try {
      const {
        context,
        syncStorage = this.syncStorageMode,
        connect = true,
        fetch = false,
        uhConversationId,
      } = params;
      if (!token || !user) {
        const {token: fetchedToken, identity} = await this.fetchToken();
        token = fetchedToken;
        if (!user) {
          user = this.userForIdentity(identity);
        }
      }

      if (!id) {
        const {conversationId} = await this.createTwilioConversation(
          context,
          uhConversationId,
        );
        id = conversationId;
      }

      const initializeStartMs = Date.now();
      const initResp = await this.service.initialize({token});
      this.metrics.timing(
        METRIC.service_init_duration,
        Date.now() - initializeStartMs,
        METRIC_TAGS,
      );

      if (initResp.error) {
        this.status$.next(ClientStatus.Error);
        this.incrementAndTrack(METRIC.service_init_error);
        return this.error(
          400,
          `error initializing client during start conversation: ${initResp.error.message}`,
        );
      }

      const conversation = new Conversation(this, {
        id,
        token,
        user,
        syncStorage,
        context: {...context},
      });

      if (connect) {
        await conversation.connect({fetch});
      }

      this.cacheParticipant(id, user);

      this.incrementAndTrack(METRIC.start_conversation_success);

      return {code: 200, data: {conversation}};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = await this.reportAndEmitException(
        `exception starting conversation: ${(e as Error).message}`,
      );

      this.incrementAndTrack(METRIC.start_conversation_error);

      return clientErrorResponse;
    }
  }

  async leaveConversation(
    params: LeaveConversationParams,
  ): Promise<ClientResponse> {
    const {participant, conversationId} = params;

    try {
      const endChatResp = await this.service.sendUserEndedChatMessage({
        conversationId,
      });

      if (endChatResp.error) {
        return this.stpError(
          `sendUserEndedChatMessage failed when leaving conversation: ${endChatResp.error.message}`,
        );
      }

      const leaveConversationResp = await this.service.leaveConversation({
        conversationId,
      });

      if (leaveConversationResp.error) {
        return this.stpError(
          `leaveConversation failed when leaving conversation: ${leaveConversationResp.error.message}`,
        );
      }

      this.emitNewConversationEvent({
        name: 'conversation-participant-left',
        conversationId,
        participant,
      });

      return {code: 200};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = await this.reportAndEmitException(
        `exception leaving conversation: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  async getConversationMessages(
    params: GetConversationMessagesParams,
  ): Promise<GetConversationMessagesResponse> {
    const {conversationId, messageIds} = params;

    try {
      if (!messageIds) {
        return {code: 200, data: {messages: {}}};
      }

      const messages: Record<string, ConversationMessage> = {};

      messageIds.forEach((messageId) => {
        const cached = this.getCachedMessage(conversationId, messageId);
        if (cached) {
          messages[messageId] = cached;
        }
      });

      while (size(messages) < messageIds.length) {
        const anchor = this.messageAnchorMemo[conversationId] || 0;

        // eslint-disable-next-line no-await-in-loop
        const getMessagesResp = await this.service.getMessages({
          conversationId,
          // the index of the starting index for the next page
          anchor,
          // read from oldest to newest messages
          direction: 'forward',
          // pageSize is 30 by default
          pageSize: undefined,
        });

        if (getMessagesResp.error) {
          return this.stpError(
            `getMessages failed when fetching messages: ${getMessagesResp.error.message}`,
          );
        }

        if (!getMessagesResp.data?.items?.length) {
          break;
        }

        this.messageAnchorMemo[conversationId] =
          anchor + (getMessagesResp.data?.items?.length || 0);

        // eslint-disable-next-line no-await-in-loop
        await Promise.all(
          getMessagesResp.data?.items.map(async (item) => {
            let participant: ConversationParticipant;
            if (!item.participant) {
              participant = {
                id: item.participantSid || 'support',
                role: 'agent',
                displayName: 'Stripe Support',
              };
            } else {
              participant = this.fromTwilioParticipant(item.participant);
            }

            const id = item.sid || item.index.toString();
            const message: ConversationMessage =
              item.type === 'text'
                ? {
                    id,
                    timestamp: item.dateCreated?.getTime() || 0,
                    text: item.body || '',
                    participant,
                    status: ConversationMessageStatus.Sent,
                    nonce: item.attributes?.nonce,
                    type: 'text-conversation-message',
                    originalText:
                      item.attributes?.translationData?.originalText,
                  }
                : {
                    id,
                    timestamp: item.dateCreated?.getTime() || 0,
                    participant,
                    status: ConversationMessageStatus.Sent,
                    nonce: item.attributes?.nonce,
                    type: 'attachment-conversation-message',
                    attachment: {
                      name: item.attachedMedia?.[0].filename || '',
                      size: item.attachedMedia?.[0].size || 0,
                      type: item.attachedMedia?.[0].contentType || '',
                      url: item.attachedMedia?.[0].url,
                    },
                  };
            this.cacheMessage(conversationId, message);

            if (messageIds.includes(id)) {
              messages[id] = message;
            }
          }),
        );
      }

      return {code: 200, data: {messages}};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = this.reportAndEmitException(
        `exception fetching messages: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  async getConversationParticipants(
    params: GetConversationParticipantsParams,
  ): Promise<GetConversationParticipantsResponse> {
    const {conversationId, participantIds} = params;

    try {
      if (!participantIds) {
        return {code: 200, data: {participants: {}}};
      }

      const participants = await Promise.all(
        participantIds.map((participantId) => {
          return this.getOrFetchParticipant(conversationId, participantId);
        }),
      );

      const missingParticipant = difference(
        participantIds,
        participants.map((p) => p?.id),
      );
      if (missingParticipant.filter(Boolean).length > 0) {
        return this.error(
          404,
          `participants [${missingParticipant}] not found`,
        );
      }

      return {
        code: 200,
        data: {
          participants: participants.reduce((acc, cur) => {
            if (cur) {
              acc[cur.id] = cur;
            }
            return acc;
          }, {} as Record<string, ConversationParticipant>),
        },
      };
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = this.reportAndEmitException(
        `exception fetching participants: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  async sendConversationMessage(
    params: SendConversationMessageParams,
  ): Promise<ClientResponse> {
    const {conversationId, message} = params;

    try {
      let response;

      switch (message.type) {
        case 'text-conversation-message': {
          response = await this.service.sendMessage({
            conversationId,
            message: message.text,
            nonce: message.nonce,
          });
          break;
        }
        case 'attachment-conversation-message': {
          if (!message.attachment.file) {
            return this.error(400, 'File not provided when sending attachment');
          }
          /*
           * We read the file into a new file to work around an issue with file
           * uploads and Chrome's enforcement of the same-origin policy. See
           * MSE-204 for details.
           */
          response = await this.service.sendFile({
            conversationId,
            file: new File(
              [message.attachment.file],
              message.attachment.file.name,
              {
                type: message.attachment.file.type,
              },
            ),
            nonce: message.nonce,
          });
          break;
        }
        default: {
          return this.error(
            400,
            'Unexpected message type when sending message',
          );
        }
      }

      if (response.error) {
        return this.stpError(
          `sendMessage failed when sending message: ${response.error.message}`,
        );
      }

      return {code: 200};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = this.reportAndEmitException(
        `exception sending conversation message: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  async sendConversationTyping(
    params: SendConversationTypingParams,
  ): Promise<ClientResponse> {
    const {conversationId} = params;

    try {
      const response = await this.service.sendTyping({conversationId});
      if (response.error) {
        return this.stpError(
          `sendTyping failed when sending typing event: ${response.error.message}`,
        );
      }

      return {code: 200};
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = this.reportAndEmitException(
        `exception sending typing event: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  async action(params: ConversationActionParams): Promise<ClientResponse> {
    const {conversationId, action} = params;
    const actionName = action.name as string;
    const extras = {conversationId, actionName};
    this.reportError('action not supported', extras);
    this.incrementAndTrack(METRIC.action_not_supported, {actionName});
    return this.error(400, 'action not supported');
  }

  async inProgress(params: InProgressParams): Promise<InProgressResponse> {
    const {conversationId} = params;
    if (!conversationId) {
      return {code: 200, data: {inProgress: false}};
    }

    try {
      const {token} = await this.fetchToken();

      const initializeStartMs = Date.now();
      const initResp = await this.service.initialize({token});
      this.metrics.timing(
        METRIC.service_init_duration,
        Date.now() - initializeStartMs,
        METRIC_TAGS,
      );

      if (initResp.error) {
        this.status$.next(ClientStatus.Error);
        this.incrementAndTrack(METRIC.service_init_error);
        return this.error(
          400,
          `Error initializing client during in progress: ${initResp.error.message}`,
        );
      }

      const getConversationResp = await this.service.getConversation({
        conversationId,
      });

      if (getConversationResp.error) {
        return this.error(400, getConversationResp.error.message);
      }

      const inProgress =
        getConversationResp.data?.attributes?.status === 'ACTIVE';

      return {
        code: 200,
        data: {
          conversationId,
          channel: this.channel,
          clientType: this.type,
          inProgress,
        },
      };
    } catch (e) {
      if (isNetworkError(e)) {
        // return a timeout error instead of throwing for network errors
        return this.error(408, (e as Error).message);
      }

      const clientErrorResponse = this.reportAndEmitException(
        `exception checking in progress: ${(e as Error).message}`,
      );

      return clientErrorResponse;
    }
  }

  /* ==================================================================================== */
  /**
   * STP handlers for events emitted by TwilioConversations.
   */
  /* ==================================================================================== */

  handleSTPEvent = (message: STPTwilioOutput): void => {
    if ('event' in message) {
      const {event} = message;

      switch (event) {
        case 'connectionStateChanged':
          this.handleSTPConnectionStateChanged(message.data);
          break;
        case 'initFailed':
          this.handleSTPInitFailed(message.data);
          break;
        case 'participantJoined':
          this.handleSTPParticipantJoined(message.data);
          break;
        case 'participantLeft':
          this.handleSTPParticipantLeft(message.data);
          break;
        case 'messageAdded':
          this.handleSTPMessageAdded(message.data);
          break;
        case 'typingStarted':
          this.handleSTPTypingStarted(message.data);
          break;
        case 'typingEnded':
          this.handleSTPTypingEnded(message.data);
          break;
        case 'conversationUpdated':
          this.handleSTPConversationUpdated(message.data);
          break;
        default:
      }
    }
  };

  private async handleSTPConnectionStateChanged(
    connectionState: TwilioConnectionState,
  ) {
    switch (connectionState) {
      case 'connecting': {
        const prev = this.getStatus();
        if (
          prev === ClientStatus.Connecting ||
          prev === ClientStatus.Reconnecting
        ) {
          break;
        }

        let next: ClientStatus;
        if (
          prev === ClientStatus.Uninitialized ||
          prev === ClientStatus.Initialized
        ) {
          next = ClientStatus.Connecting;
        } else {
          next = ClientStatus.Reconnecting;
        }
        this.status$.next(next);
        break;
      }
      case 'connected': {
        this.status$.next(ClientStatus.Connected);
        break;
      }
      case 'disconnecting':
      case 'disconnected': {
        await this.disconnect();
        break;
      }
      case 'denied': {
        this.status$.next(ClientStatus.Reconnecting);
        this.incrementAndTrack(METRIC.connection_state_denied);
        // refresh token and reconnect
        break;
      }
      default:
    }
  }

  private async handleSTPInitFailed(error?: {
    terminal: boolean;
    message: string;
  }) {
    if (error) {
      const {terminal, message} = error;
      if (terminal) {
        this.status$.next(ClientStatus.Uninitialized);
        this.stpError(`Twilio initialization failed ${message}`);
      }
    }
  }

  private async handleSTPParticipantJoined(participant: TwilioParticipant) {
    const {conversationId} = participant;

    try {
      const conversationParticipant = this.fromTwilioParticipant(participant);

      this.cacheParticipant(conversationId, conversationParticipant);

      this.emitNewConversationEvent({
        name: 'conversation-participant-joined',
        conversationId,
        participant: conversationParticipant,
      });
    } catch (e) {
      if (isNetworkError(e)) {
        return;
      }

      this.reportAndEmitException(
        `exception handling participant joined event: ${(e as Error).message}`,
        undefined, // Sentry tags
        false, // Not a 5xx Error
      );
    }
  }

  private async handleSTPParticipantLeft(participant: TwilioParticipant) {
    const {conversationId} = participant;

    try {
      const conversationParticipant = this.fromTwilioParticipant(participant);

      this.emitNewConversationEvent({
        name: 'conversation-participant-left',
        conversationId,
        participant: conversationParticipant,
      });
    } catch (e) {
      if (isNetworkError(e)) {
        return;
      }

      this.reportAndEmitException(
        `exception handling participant left event: ${(e as Error).message}`,
        undefined, // Sentry tags
        false, // Not a 5xx error
      );
    }
  }

  private handleSTPMessageAdded(message: TwilioMessage) {
    if (message.attributes?.notification) {
      // these events are apparently ignored as they are member joining/leaving events
      return;
    }
    const messageId = message.sid ? message.sid : message.index.toString();
    this.emitNewConversationEvent({
      name: 'conversation-message-added',
      conversationId: message.conversationId,
      participantId: message.author!,
      messageId,
      nonce: message.attributes?.nonce,
    });
  }

  private handleSTPTypingStarted(participant: TwilioParticipant) {
    const participantId = participant.identity;

    if (participantId) {
      this.emitNewConversationEvent({
        name: 'conversation-typing-started',
        conversationId: participant.conversationId,
        participantId,
      });
    }
  }

  private handleSTPTypingEnded(participant: TwilioParticipant) {
    const participantId = participant.identity;

    if (participantId) {
      this.emitNewConversationEvent({
        name: 'conversation-typing-ended',
        conversationId: participant.conversationId,
        participantId,
      });
    }
  }

  private handleSTPConversationUpdated({
    conversation,
    updateReasons,
  }: {
    conversation: TwilioConversation;
    updateReasons: TwilioConversationUpdateReason[];
  }) {
    if (updateReasons.includes('attributes')) {
      let status: ConversationStatus = ConversationStatus.Active;
      if (conversation.attributes?.status === 'INACTIVE') {
        status = ConversationStatus.Inactive;
      }

      this.emitNewConversationEvent({
        name: 'conversation-updated',
        conversationId: conversation.sid,
        status,
      });
    }
  }

  /* ==================================================================================== */
  /**
   * Private helper methods
   */
  /* ==================================================================================== */

  private stpError(error: string): Promise<ClientErrorResponse> {
    this.incrementAndTrack(METRIC.stp_error);
    // 5xx error will reportError
    return this.error(500, `STPError ${error}`);
  }

  private reportError(
    error: string,
    extras?: Record<string, string>,
    tags?: ErrorScope['tags'],
  ) {
    return this.errors.reportError(`${LOG_PREFIX} ${error}`, {
      extras,
      tags: {...ERROR_TAGS, ...tags},
    });
  }

  protected error(
    code: number,
    error: string,
    tags?: ErrorScope['tags'],
  ): Promise<ClientErrorResponse> {
    return super.error(code, `${LOG_PREFIX} ${error}`, {
      ...ERROR_TAGS,
      ...tags,
    });
  }

  private fetchToken = async (): Promise<{token: string; identity: string}> => {
    if (!this.tokenUrl) {
      throw new Error('token URL not present');
    }

    const resp = await this.fetch(this.tokenUrl);
    const data = await resp.json();

    const {token, identity} = data;

    return {token, identity};
  };

  private userForIdentity = (
    identity: string,
    role: 'user' | 'agent' = 'user',
  ): ConversationParticipant => {
    return {
      role,
      id: identity,
      displayName: identity,
    };
  };

  private async getOrFetchParticipant(
    conversationId: string,
    participantId: string,
  ): Promise<ConversationParticipant | undefined> {
    const cached = this.getCachedParticipant(conversationId, participantId);

    if (cached) {
      return cached;
    }

    return this.fetchParticipant(conversationId, participantId);
  }

  private async fetchParticipant(
    conversationId: string,
    participantId: string,
  ): Promise<ConversationParticipant | undefined> {
    const participantResp = await this.service.getParticipant({
      identity: participantId,
      conversationId,
    });
    if (participantResp.error) {
      return;
    }

    const twilioParticipant = participantResp.data;
    if (!twilioParticipant) {
      return;
    }

    const conversationParticipant =
      this.fromTwilioParticipant(twilioParticipant);

    this.cacheParticipant(conversationId, conversationParticipant);

    return conversationParticipant;
  }

  private fromTwilioParticipant(
    participant: TwilioParticipant,
  ): ConversationParticipant {
    const {identity} = participant;
    if (!identity) {
      throw Error('participant has no identity');
    }

    const role =
      participant.attributes?.member_type === 'agent' ? 'agent' : 'user';
    let displayName;
    if (role === 'agent') {
      displayName =
        participant.user?.friendlyName?.split(' ')[0] || 'Stripe support';
    } else {
      displayName = participant.user?.friendlyName || 'User';
    }

    return {
      id: identity,
      role,
      displayName,
    };
  }

  private createTwilioConversation = async (
    context: ConversationContext = {},
    uhConversationId: string | undefined = undefined,
  ): Promise<{conversationId: string}> => {
    if (!this.conversationUrl) {
      throw new Error('not implemented');
    }

    const analyticsEventId = await this.analytics.requestAnalyticsEventId();

    const {topic, audience, entryPoint} = context;

    const body: Record<string, any> = {
      topic,
      audience,
      entrypoint: entryPoint,
      channel_type: 'live_chat',
      analytics_event_id: analyticsEventId,
      universal_help_conversation_id: uhConversationId,
    };

    if (context?.providerArgs) {
      body.provider_args = context.providerArgs;
    }

    const resp = await this.fetch(this.conversationUrl, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await resp.json();

    const {conversationId} = data;

    return {conversationId};
  };

  private incrementAndTrack(
    name: string,
    params: Record<string, string> = {},
  ): void {
    const metricParams = {...METRIC_TAGS, ...params};
    this.metrics.increment(name, metricParams);
    this.analytics.track(name, metricParams);
  }

  private cacheMessage(
    conversationId: string,
    message: ConversationMessage,
  ): void {
    const key = `${conversationId}-${message.id}`;
    this.messageCache.set(key, message);
  }

  private getCachedMessage(
    conversationId: string,
    messageId: string,
  ): ConversationMessage | undefined {
    const key = `${conversationId}-${messageId}`;
    return this.messageCache.get(key);
  }

  private async reportAndEmitException(
    error: string,
    tags?: ErrorScope['tags'],
    is5xxErrorCode = true,
  ): Promise<ClientErrorResponse> {
    let clientErrorResponse: ClientErrorResponse;
    const errorTags = {
      ...tags,
      assistant_error_screen: true,
    };
    if (is5xxErrorCode) {
      // 5xx error will reportError
      clientErrorResponse = await this.error(500, error, errorTags);
    } else {
      const eventId = this.reportError(error, undefined, errorTags);

      clientErrorResponse = {
        // this code value satisfies the return type, however, currently all instances of non 5xx errors
        // do not use this return value, they just fire-and-forget.
        code: 400,
        error,
        sentryEventId: eventId,
      };
    }

    this.emitNewEvent({
      name: 'client-exception',
      error,
      sentryEventId: clientErrorResponse.sentryEventId,
    });

    return clientErrorResponse;
  }
}
